<template>
  <app-card :border-color="isNotPayable && !isLoading ? 'error' : 'neutral'">
    <div class="flex justify-between">
      <div class="flex gap-4">
        <app-icon
          name="building"
          variant="solid"
          size="lg"
          class="shadow-xs"
        />

        <div
          v-if="isLoading"
          class="flex flex-col gap-2"
        >
          <div class="skeleton w-48 h-5" />
          <div class="skeleton w-48 h-3" />
        </div>
        <div
          v-else
          class="flex flex-col"
          data-sentry-unmask
        >
          <p class="font-semibold">{{ agency?.data?.name }}</p>
          <span class="text-sm text-gray-600">
            {{ `Available since ${formatDate(agency?.data?.created_at)}` }}
          </span>
        </div>
      </div>

      <agency-payable-icon
        v-if="!isLoading"
        size="sm"
        :status="status"
      />
    </div>

    <div class="space-y-4 mt-6">
      <div
        v-if="isLoading"
        class="skeleton h-32 mt-6"
      />

      <app-alert
        v-else-if="status !== PayableStatus.PAYABLE"
        :heading="alertMessage.heading"
        icon="alert-circle"
      >
        <p>{{ alertMessage.message }}</p>
      </app-alert>

      <slot name="actions">
        <button
          v-if="canSetupAgencies && !isLoading && !hideDefaultActions"
          class="btn btn-sm btn-secondary w-full"
          @click="goToAgency"
        >
          <app-icon
            name="settings"
            size="sm"
          />
          Agency settings
        </button>
      </slot>
    </div>
  </app-card>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { formatDate } from '@/utils/dates';
import { useAuthStore } from '@/stores/auth';
import { PayableStatus } from '@/constants/payableStatus';
import { useAgenciesStore } from '@/stores/agencies';
import { useAgencyDrawerStore } from '@/stores/agencies';
import AgencyPayableIcon from '@/components/agencies/AgencyPayableIcon.vue';

const emit = defineEmits(['close']);

const props = defineProps({
  agency: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  hideDefaultActions: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const authStore = useAuthStore();
const { setAtomic } = useAgenciesStore();
const agencyDrawerStore = useAgencyDrawerStore();

const status = computed(() => props.agency?.data?.identifier_coverage);
const isNotPayable = computed(() => status.value === PayableStatus.NOT_PAYABLE);

const alertMessage = computed(() => {
  switch (status.value) {
    case PayableStatus.PARTIALLY_PAYABLE:
      return {
        heading: 'Some identifiers are not payable',
        message:
          "The agency hasn't linked all identifiers to a beneficiary. Reservations tied to them can't be paid until they do.",
      };
    case PayableStatus.NOT_PAYABLE:
      return {
        heading: 'Agency not payable',
        message:
          'This agency is not payable because it has no linked beneficiaries. Reservations tied to this agency cannot be paid.',
      };
    default:
      return '';
  }
});

const canSetupAgencies = computed(() => {
  return authStore.hasAllPermissions(['agency:setup', 'agency:list']);
});

function goToAgency() {
  setAtomic('shouldResetFilters', false);
  agencyDrawerStore.closeDrawer();
  emit('close');
  router.push({ name: 'agency-details' });
}
</script>
